import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './utils/api';
import './RoleManagement.css';

const ROLE_LABELS = { admin: 'Org-Admin', tester: 'Sys-Admin', user: 'Viewer' };

export default function RoleManagement() {
  const [rolesData, setRolesData] = useState(null);
  const [allPerms, setAllPerms] = useState([]);
  // P1.5 — what THIS caller may grant. The server enforces it (roles.py
  // returns 403 'Not permitted to grant'); this only stops the UI offering
  // a button that cannot work. Defaults to allPerms so an older server
  // response degrades to the previous behaviour rather than disabling
  // everything.
  const [grantablePerms, setGrantablePerms] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [effectivePerms, setEffectivePerms] = useState({});
  const [pendingOverrides, setPendingOverrides] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadRoles = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/roles');
      if (res.ok) {
        const data = await res.json();
        setRolesData(data.roles || {});
        setAllPerms(data.all_permissions || []);
        setGrantablePerms(data.grantable_permissions || null);
      }
    } catch { setError('Failed to load roles'); }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/users?per_page=100');
      if (res.ok) {
        const data = await res.json();
        setUsers(data.items || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadRoles(); loadUsers(); }, [loadRoles, loadUsers]);

  const selectUser = async (userId) => {
    if (!userId) { setSelectedUser(null); setEffectivePerms({}); setPendingOverrides({}); return; }
    const u = users.find(x => x.id === userId);
    setSelectedUser(u);
    setPendingOverrides({});
    try {
      const res = await apiFetch(`/api/v1/admin/users/${userId}/permissions`);
      if (res.ok) {
        const data = await res.json();
        setEffectivePerms(data.permissions || {});
      }
    } catch { setError('Failed to load permissions'); }
  };

  const toggleOverride = (perm) => {
    const current = effectivePerms[perm];
    const currentGranted = current?.granted ?? false;
    setPendingOverrides(prev => {
      const next = { ...prev };
      if (perm in next) {
        delete next[perm]; // undo pending change
      } else {
        next[perm] = !currentGranted;
      }
      return next;
    });
  };

  const saveOverrides = async () => {
    if (!selectedUser || Object.keys(pendingOverrides).length === 0) return;
    setError(''); setMessage('');
    try {
      const res = await apiFetch(`/api/v1/admin/users/${selectedUser.id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: pendingOverrides }),
      });
      if (res.ok) {
        const data = await res.json();
        setEffectivePerms(data.permissions || {});
        setPendingOverrides({});
        setMessage('Permissions updated');
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Failed to save');
      }
    } catch { setError('Save failed'); }
  };

  const resetOverrides = async () => {
    if (!selectedUser) return;
    if (!window.confirm('Remove all overrides for this user? They will revert to base role permissions.')) return;
    try {
      // apiFetch DOES NOT THROW ON 4xx/5xx. This loop used to discard every
      // response, so a DELETE that was refused — the delete route rejects
      // cross-org targets with no root bypass — left the override in place
      // while the admin was told "Reset to default role permissions".
      //
      // On a PERMISSIONS screen a false success is the worst possible outcome:
      // the admin believes elevated access was removed and stops looking, and
      // the override is exactly the thing that grants access ahead of the base
      // role. Report which ones survived, by name.
      const failed = [];
      for (const perm of allPerms) {
        if (effectivePerms[perm]?.source === 'override') {
          const res = await apiFetch(
            `/api/v1/admin/users/${selectedUser.id}/permissions/${perm}`,
            { method: 'DELETE' },
          );
          if (!res.ok) failed.push(perm);
        }
      }
      await selectUser(selectedUser.id);
      if (failed.length) {
        setError(
          `${failed.length} override(s) could NOT be removed and are still in `
          + `effect: ${failed.map(formatPerm).join(', ')}`,
        );
      } else {
        setMessage('Reset to default role permissions');
      }
    } catch { setError('Reset failed'); }
  };

  const formatPerm = (p) => p.replace('access:', '').replace(/_/g, ' ');

  return (
    <div className="role-mgmt">
      <h1>Role Management</h1>
      {error && <div className="rm-error">{error}</div>}
      {message && <div className="rm-msg">{message}</div>}

      {/* Base Role Matrix */}
      <section className="rm-section">
        <h2>Base Role Permissions</h2>
        {rolesData && (
          <table className="rm-matrix">
            <thead>
              <tr>
                <th>Permission</th>
                {Object.keys(rolesData).map(r => <th key={r} className={`role-col ${r}`}>{ROLE_LABELS[r] || r}</th>)}
              </tr>
            </thead>
            <tbody>
              {allPerms.map(perm => (
                <tr key={perm}>
                  <td className="perm-name">{formatPerm(perm)}</td>
                  {Object.entries(rolesData).map(([role, data]) => (
                    <td key={role} className="perm-cell">
                      {data.permissions?.[perm] ? <span className="check">✓</span> : <span className="cross">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Per-User Overrides */}
      <section className="rm-section">
        <h2>User Permission Overrides</h2>
        <div className="user-picker">
          <label>Select User:</label>
          <select onChange={e => selectUser(e.target.value)} value={selectedUser?.id || ''}>
            <option value="">— Choose —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.email}) — {ROLE_LABELS[u.role] || u.role}</option>)}
          </select>
        </div>

        {selectedUser && (
          <>
            <div className="selected-user-info">
              <span>{selectedUser.full_name}</span>
              <span className={`role-chip ${selectedUser.role}`}>{ROLE_LABELS[selectedUser.role] || selectedUser.role}</span>
            </div>

            <table className="rm-overrides">
              <thead>
                <tr><th>Permission</th><th>Granted</th><th>Source</th><th>Override</th></tr>
              </thead>
              <tbody>
                {allPerms.map(perm => {
                  const eff = effectivePerms[perm] || {};
                  const hasPending = perm in pendingOverrides;
                  const displayGranted = hasPending ? pendingOverrides[perm] : eff.granted;
                  return (
                    <tr key={perm} className={hasPending ? 'pending' : ''}>
                      <td className="perm-name">{formatPerm(perm)}</td>
                      <td><span className={displayGranted ? 'granted' : 'denied'}>{displayGranted ? '✓' : '✗'}</span></td>
                      <td>
                        <span className={`source-badge ${eff.source}`}>
                          {hasPending ? 'pending' : eff.source}
                        </span>
                      </td>
                      <td>
                        {/* Revoking is always allowed - it only reduces access, and
                            revoking a tester's Test Runner is a supported use. Only
                            GRANTING is restricted, so disable solely in that direction. */}
                        <button
                          className={`toggle-btn ${displayGranted ? 'on' : 'off'}`}
                          onClick={() => toggleOverride(perm)}
                          disabled={!displayGranted && grantablePerms !== null
                                    && !grantablePerms.includes(perm)}
                          title={!displayGranted && grantablePerms !== null
                                 && !grantablePerms.includes(perm)
                                 ? 'This permission can only be granted by a site administrator'
                                 : undefined}
                        >
                          {displayGranted ? 'Revoke' : 'Grant'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="rm-actions">
              <button className="btn-primary" onClick={saveOverrides}
                disabled={Object.keys(pendingOverrides).length === 0}>
                Save Changes ({Object.keys(pendingOverrides).length})
              </button>
              <button className="btn-secondary" onClick={resetOverrides}>Reset to Default</button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
